import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Phone, Mail, Menu, X, ChevronDown, ArrowRight, Settings2, FileText } from "lucide-react";
import { BEDRIJF, SLAGBOMEN, TOEGANG, SECTOREN } from "../lib/content";

const NAV = [
  { label: "Slagbomen", to: "/slagbomen", mega: SLAGBOMEN.map((s) => ({ label: s.naam, to: `/slagbomen#${s.slug}` })) },
  { label: "Toegangsoplossingen", to: "/toegangsoplossingen", mega: TOEGANG.map((t) => ({ label: t.naam, to: `/toegangsoplossingen#${t.slug}` })).concat([{ label: "Verkeerslichten & detectie", to: "/toegangsoplossingen#veiligheid" }, { label: "Beheer op afstand", to: "/toegangsoplossingen#beheer" }]) },
  { label: "Sectoren", to: "/sectoren", mega: SECTOREN.map((s) => ({ label: s.naam, to: `/sectoren/${s.slug}` })) },
  { label: "Werkwijze", to: "/werkwijze" },
  { label: "Onderhoud", to: "/onderhoud" },
  { label: "Kennisbank", to: "/kennisbank" },
  { label: "Contact", to: "/contact" },
];

function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [mega, setMega] = useState(null);
  const loc = useLocation();

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);
  useEffect(() => { setOpen(false); setMega(null); }, [loc.pathname]);

  return (
    <header className={`fixed top-0 inset-x-0 z-50 border-b border-titanium/10 bg-obsidian/85 backdrop-blur-xl transition-[padding] duration-300`} onMouseLeave={() => setMega(null)}>
      <div className={`mx-auto max-w-[1440px] px-5 sm:px-8 flex items-center justify-between gap-6 transition-[height] duration-300 ${scrolled ? "h-16" : "h-20"}`}>
        <Link to="/" data-testid="header-logo" className="group shrink-0">
          <div className="flex items-center gap-3">
            <svg width="30" height="30" viewBox="0 0 32 32" fill="none" aria-hidden="true">
              <rect x="2" y="22" width="6" height="8" stroke="#2B9FD7" strokeWidth="1.5" />
              <line x1="6" y1="22" x2="28" y2="8" stroke="#F4F7F9" strokeWidth="2.5" />
              <circle cx="27" cy="8" r="2.4" fill="#35C98B" />
            </svg>
            <div>
              <p className="font-heading font-semibold tracking-[0.18em] text-offwhite text-sm leading-none">SLAGBOOM EXPERT</p>
              <p className="font-label text-[8px] text-titanium/60 mt-1">POWERED BY BEVEILIG ALLES B.V.</p>
            </div>
          </div>
        </Link>

        <nav className="hidden xl:flex items-center gap-1" aria-label="Hoofdnavigatie">
          {NAV.map((item) => (
            <div key={item.label} className="relative" onMouseEnter={() => setMega(item.mega ? item.label : null)}>
              <NavLink to={item.to} data-testid={`nav-${item.label.toLowerCase()}`}
                className={({ isActive }) => `flex items-center gap-1 px-3 py-2 text-[13px] font-medium transition-colors duration-200 ${isActive ? "text-electric" : "text-titanium hover:text-offwhite"}`}>
                {item.label}{item.mega && <ChevronDown size={13} strokeWidth={1.5} />}
              </NavLink>
            </div>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4 shrink-0">
          <a href={BEDRIJF.telefoonLink} data-testid="header-phone" className="flex items-center gap-2 text-sm text-titanium hover:text-electric transition-colors duration-200 font-mono">
            <Phone size={15} strokeWidth={1.5} />{BEDRIJF.telefoon}
          </a>
          <Link to="/configurator" data-testid="header-cta" className="btn-primary !px-5 !py-2.5 text-xs">Stel uw slagboom samen</Link>
        </div>

        <button className="xl:hidden text-offwhite p-2" data-testid="mobile-menu-toggle" onClick={() => setOpen(!open)} aria-label="Menu">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* megamenu */}
      {mega && (
        <div className="hidden xl:block absolute inset-x-0 top-full border-b border-titanium/10 bg-carbon/98 backdrop-blur-xl" data-testid="megamenu">
          <div className="mx-auto max-w-[1440px] px-8 py-8 grid grid-cols-4 gap-x-8 gap-y-2">
            {NAV.find((n) => n.label === mega)?.mega.map((m) => (
              <Link key={m.label} to={m.to} className="group flex items-center justify-between border-b border-titanium/10 py-3 text-[13px] text-titanium hover:text-electric transition-colors duration-200">
                {m.label}
                <ArrowRight size={13} strokeWidth={1.5} className="opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* mobile menu */}
      {open && (
        <div className="xl:hidden border-t border-titanium/10 bg-obsidian max-h-[calc(100vh-4rem)] overflow-y-auto" data-testid="mobile-menu">
          <div className="px-5 py-6 space-y-1">
            {NAV.map((item) => (
              <NavLink key={item.label} to={item.to} className="block py-3 border-b border-titanium/10 text-offwhite font-heading text-lg">{item.label}</NavLink>
            ))}
            <div className="pt-4 flex flex-col gap-3">
              <Link to="/configurator" className="btn-primary">Stel uw slagboom samen</Link>
              <a href={BEDRIJF.telefoonLink} className="btn-ghost"><Phone size={15} /> {BEDRIJF.telefoon}</a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

function Footer() {
  const jaar = new Date().getFullYear();
  const kol = [
    { titel: "Oplossingen", links: [["Slagbomen", "/slagbomen"], ["ANPR", "/toegangsoplossingen#anpr"], ["Intercom", "/toegangsoplossingen#video-intercom"], ["Taglezers", "/toegangsoplossingen#tag-pas"], ["Veiligheid", "/slagbomen#veiligheid"], ["Configurator", "/configurator"]] },
    { titel: "Sectoren", links: [["Bedrijfsterreinen", "/sectoren/bedrijfsterreinen"], ["Logistiek", "/sectoren/logistiek"], ["Parkeerterreinen", "/sectoren/parkeerterreinen"], ["VvE", "/sectoren/vve"], ["Parkeergarages", "/sectoren/parkeergarages"], ["Industrie", "/sectoren/industrie-havens"], ["Recycling", "/sectoren/recycling"]] },
    { titel: "Service", links: [["Werkwijze", "/werkwijze"], ["Onderhoud", "/onderhoud"], ["Storing melden", "/onderhoud#storing"], ["Kennisbank", "/kennisbank"], ["Offerte", "/offerte"], ["Contact", "/contact"]] },
    { titel: "Juridisch", links: [["Privacyverklaring", "/privacy"], ["Privacy bij ANPR", "/privacy-anpr"], ["Cookieverklaring", "/cookies"], ["Disclaimer", "/disclaimer"], ["Veelgestelde vragen", "/faq"]] },
  ];
  return (
    <footer className="border-t-2 border-brand/40 band-blue-soft relative noise">
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 py-16 sm:py-20">
        <div className="grid gap-12 lg:grid-cols-[1.3fr_repeat(4,1fr)]">
          <div>
            <p className="font-heading font-semibold tracking-[0.18em] text-offwhite">SLAGBOOM EXPERT</p>
            <p className="mt-2 text-sm text-titanium/80">Complete voertuigtoegang door {BEDRIJF.moeder}</p>
            <div className="mt-6 space-y-2 text-sm">
              <a href={BEDRIJF.telefoonLink} data-testid="footer-phone" className="flex items-center gap-2 text-titanium hover:text-electric transition-colors duration-200 font-mono"><Phone size={14} strokeWidth={1.5} />{BEDRIJF.telefoon}</a>
              <a href={`mailto:${BEDRIJF.email}`} data-testid="footer-email" className="flex items-center gap-2 text-titanium hover:text-electric transition-colors duration-200 font-mono"><Mail size={14} strokeWidth={1.5} />{BEDRIJF.email}</a>
              <a href={`https://${BEDRIJF.site}`} target="_blank" rel="noreferrer" className="block text-titanium hover:text-electric transition-colors duration-200 font-mono text-xs mt-1">{BEDRIJF.site}</a>
            </div>
          </div>
          {kol.map((k) => (
            <div key={k.titel}>
              <p className="font-label text-[10px] text-brand mb-5">{k.titel}</p>
              <ul className="space-y-2.5">
                {k.links.map(([label, to]) => (
                  <li key={label}><Link to={to} className="text-sm text-titanium hover:text-offwhite transition-colors duration-200">{label}</Link></li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="mt-14 border-t border-titanium/10 pt-8 space-y-3">
          <p className="text-xs text-titanium/60 max-w-3xl">Producteigenschappen, integraties en mogelijkheden verschillen per merk, model en configuratie. Iedere installatie wordt projectmatig beoordeeld.</p>
          <p className="text-xs text-titanium/60">Slagboom Expert is een gespecialiseerde dienstverlening van Beveilig Alles B.V.</p>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs text-titanium/50">© {jaar} Beveilig Alles B.V. Alle rechten voorbehouden.</p>
            <Link to="/admin" className="text-[10px] font-label text-titanium/30 hover:text-titanium/60 transition-colors duration-200" data-testid="footer-admin-link">Beheer</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

function MobileBar() {
  return (
    <div className="fixed bottom-0 inset-x-0 z-40 grid grid-cols-3 border-t border-titanium/15 bg-obsidian/95 backdrop-blur-xl lg:hidden" data-testid="mobile-conversion-bar">
      <a href={BEDRIJF.telefoonLink} className="flex flex-col items-center gap-1 py-3 text-titanium" data-testid="mobile-bar-call">
        <Phone size={16} strokeWidth={1.5} /><span className="font-label text-[9px]">Bel direct</span>
      </a>
      <Link to="/configurator" className="flex flex-col items-center gap-1 py-3 text-obsidian bg-brand" data-testid="mobile-bar-config">
        <Settings2 size={16} strokeWidth={1.5} /><span className="font-label text-[9px]">Configureren</span>
      </Link>
      <Link to="/offerte" className="flex flex-col items-center gap-1 py-3 text-titanium" data-testid="mobile-bar-quote">
        <FileText size={16} strokeWidth={1.5} /><span className="font-label text-[9px]">Offerte</span>
      </Link>
    </div>
  );
}

function CookieBanner() {
  const [show, setShow] = useState(false);
  useEffect(() => { if (!localStorage.getItem("se_cookies")) setShow(true); }, []);
  const kies = (v) => { localStorage.setItem("se_cookies", JSON.stringify({ noodzakelijk: true, statistiek: v, marketing: v, at: Date.now() })); setShow(false); };
  if (!show) return null;
  return (
    <div className="fixed bottom-16 lg:bottom-6 left-4 right-4 lg:left-auto lg:right-6 lg:max-w-md z-50 border border-titanium/20 bg-carbon p-6 shadow-2xl" data-testid="cookie-banner">
      <p className="font-heading font-semibold text-offwhite text-sm">Cookievoorkeuren</p>
      <p className="mt-2 text-xs text-titanium leading-relaxed">Wij gebruiken noodzakelijke cookies voor het functioneren van de website. Statistiek- en marketingcookies worden pas geladen na uw toestemming. Zie de <Link to="/cookies" className="text-electric underline">cookieverklaring</Link>.</p>
      <div className="mt-4 flex gap-3">
        <button onClick={() => kies(true)} className="btn-primary !px-4 !py-2 text-xs" data-testid="cookie-accept">Alles accepteren</button>
        <button onClick={() => kies(false)} className="btn-ghost !px-4 !py-2 text-xs" data-testid="cookie-necessary">Alleen noodzakelijk</button>
      </div>
    </div>
  );
}

export default function Layout({ children }) {
  const loc = useLocation();
  useEffect(() => {
    if (!sessionStorage.getItem("se_landing")) sessionStorage.setItem("se_landing", loc.pathname + loc.search);
  }, [loc]);
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    let raf;
    const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);
  useEffect(() => {
    if (loc.hash) {
      const el = document.getElementById(loc.hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 120);
    } else window.scrollTo(0, 0);
  }, [loc.pathname, loc.hash]);

  return (
    <div className="min-h-screen bg-obsidian text-offwhite font-body">
      <Header />
      <main className="pt-20 pb-14 lg:pb-0">{children}</main>
      <Footer />
      <MobileBar />
      <CookieBanner />
    </div>
  );
}
